// Behavioral Pattern Analyzer - Analyzes study habits and engagement

/**
 * Analyzes study consistency patterns
 * @param {Object} attendanceData - Attendance records
 * @param {Object} revisionData - Revision activity data
 * @param {Object} studyStreakData - Study streak information
 * @returns {Object} Study consistency metrics
 */
export function analyzeStudyConsistency(attendanceData, revisionData, studyStreakData) {
  let consistencyScore = 0;
  let pattern = 'irregular';
  const bestStudyDays = [];
  const bestStudyTimes = [];

  // Factor 1: Attendance consistency (40% weight)
  if (attendanceData && attendanceData.records && attendanceData.records.length > 0) {
    const totalAttendance = attendanceData.records.reduce((sum, record) => {
      return sum + (record.attendance || 0);
    }, 0);
    const avgAttendance = totalAttendance / attendanceData.records.length;
    consistencyScore += (avgAttendance / 100) * 40;
  }

  // Factor 2: Study streak (30% weight)
  if (studyStreakData && studyStreakData.currentStreak) {
    const streakScore = Math.min(100, studyStreakData.currentStreak * 5);
    consistencyScore += (streakScore / 100) * 30;
    
    if (studyStreakData.currentStreak >= 7) {
      pattern = 'consistent';
    } else if (studyStreakData.currentStreak >= 3) {
      pattern = 'building';
    }
  }

  // Factor 3: Revision regularity (30% weight)
  if (revisionData && revisionData.practiceSessions) {
    const sessions = revisionData.practiceSessions;
    if (sessions.length > 0) {
      // Calculate session frequency
      const daysBetweenSessions = [];
      for (let i = 1; i < sessions.length; i++) {
        const date1 = new Date(sessions[i - 1].createdAt);
        const date2 = new Date(sessions[i].createdAt);
        const daysDiff = Math.abs((date2 - date1) / (1000 * 60 * 60 * 24));
        daysBetweenSessions.push(daysDiff);
      }

      if (daysBetweenSessions.length > 0) {
        const avgDaysBetween = daysBetweenSessions.reduce((a, b) => a + b, 0) / daysBetweenSessions.length;
        const regularityScore = Math.max(0, 100 - (avgDaysBetween * 10));
        consistencyScore += (regularityScore / 100) * 30;
      }
    }
  }

  // Determine best study days and times (placeholder - would need more detailed data)
  bestStudyDays.push('Monday', 'Wednesday', 'Friday');
  bestStudyTimes.push('Morning', 'Evening');

  return {
    score: Math.round(Math.min(100, consistencyScore)),
    pattern,
    bestStudyDays,
    bestStudyTimes
  };
}

/**
 * Analyzes engagement level across activities
 * @param {Object} doubtData - Doubt activity data
 * @param {Object} attendanceData - Attendance data
 * @param {Object} assignmentData - Assignment submission data
 * @returns {Object} Engagement metrics
 */
export function analyzeEngagementLevel(doubtData, attendanceData, assignmentData) {
  let overallEngagement = 0;
  let classParticipation = 0;
  let doubtAsking = 0;
  let revisionActivity = 0;

  // Class participation (based on attendance)
  if (attendanceData && attendanceData.records && attendanceData.records.length > 0) {
    const totalAttendance = attendanceData.records.reduce((sum, record) => {
      return sum + (record.attendance || 0);
    }, 0);
    classParticipation = Math.round(totalAttendance / attendanceData.records.length);
  }

  // Doubt asking frequency
  if (doubtData && doubtData.doubts) {
    const doubtCount = doubtData.doubts.length;
    // Normalize: 1 doubt per week = 100% engagement
    const weeksInPeriod = 26; // ~6 months
    const expectedDoubts = weeksInPeriod;
    doubtAsking = Math.min(100, Math.round((doubtCount / expectedDoubts) * 100));
  }

  // Assignment engagement
  let assignmentEngagement = 0;
  if (assignmentData && assignmentData.submissions) {
    const submissions = assignmentData.submissions;
    const onTimeSubmissions = submissions.filter(s => {
      const submitted = new Date(s.submittedAt);
      const deadline = new Date(s.deadline);
      return submitted <= deadline;
    }).length;
    
    assignmentEngagement = submissions.length > 0 
      ? Math.round((onTimeSubmissions / submissions.length) * 100)
      : 0;
  }

  // Overall engagement (weighted average)
  overallEngagement = Math.round(
    (classParticipation * 0.4) +
    (doubtAsking * 0.3) +
    (assignmentEngagement * 0.3)
  );

  return {
    overall: overallEngagement,
    classParticipation,
    doubtAsking,
    revisionActivity: revisionActivity || 50 // Placeholder
  };
}

/**
 * Detects procrastination patterns
 * @param {Object} assignmentData - Assignment submission data
 * @param {Object} todoTasksData - Todo tasks data
 * @returns {number} Procrastination index (0-100)
 */
export function detectProcrastination(assignmentData, todoTasksData) {
  let procrastinationScore = 0;
  let indicators = 0;

  // Indicator 1: Late assignment submissions
  if (assignmentData && assignmentData.submissions && assignmentData.submissions.length > 0) {
    const lateSubmissions = assignmentData.submissions.filter(s => {
      const submitted = new Date(s.submittedAt);
      const deadline = new Date(s.deadline);
      return submitted > deadline;
    }).length;

    const latePercentage = (lateSubmissions / assignmentData.submissions.length) * 100;
    procrastinationScore += latePercentage;
    indicators++;
  }

  // Indicator 2: Last-minute submissions (within 24 hours of deadline)
  if (assignmentData && assignmentData.submissions && assignmentData.submissions.length > 0) {
    const lastMinuteSubmissions = assignmentData.submissions.filter(s => {
      const submitted = new Date(s.submittedAt);
      const deadline = new Date(s.deadline);
      const hoursDiff = (deadline - submitted) / (1000 * 60 * 60);
      return hoursDiff >= 0 && hoursDiff <= 24;
    }).length;

    const lastMinutePercentage = (lastMinuteSubmissions / assignmentData.submissions.length) * 100;
    procrastinationScore += lastMinutePercentage * 0.5; // Half weight
    indicators++;
  }

  // Indicator 3: Incomplete todo tasks
  if (todoTasksData && todoTasksData.tasks && todoTasksData.tasks.length > 0) {
    const incompleteTasks = todoTasksData.tasks.filter(t => !t.completed).length;
    const incompletePercentage = (incompleteTasks / todoTasksData.tasks.length) * 100;
    procrastinationScore += incompletePercentage * 0.7;
    indicators++;
  }

  return indicators > 0 ? Math.round(Math.min(100, procrastinationScore / indicators)) : 0;
}

/**
 * Analyzes study schedule patterns
 * @param {Object} revisionData - Revision activity data
 * @param {Array} testResults - Test results
 * @returns {Object} Study schedule insights
 */
export function analyzeStudySchedule(revisionData, testResults) {
  const schedule = {
    preferredTime: 'evening',
    consistentDays: [],
    peakPerformanceTimes: [],
    studyDuration: 0
  };

  // Analyze revision session times
  if (revisionData && revisionData.practiceSessions) {
    const sessions = revisionData.practiceSessions;
    const timeSlots = { morning: 0, afternoon: 0, evening: 0, night: 0 };

    sessions.forEach(session => {
      const date = new Date(session.createdAt);
      const hour = date.getHours();

      if (hour >= 5 && hour < 12) timeSlots.morning++;
      else if (hour >= 12 && hour < 17) timeSlots.afternoon++;
      else if (hour >= 17 && hour < 21) timeSlots.evening++;
      else timeSlots.night++;
    });

    // Find preferred time
    const maxSlot = Object.entries(timeSlots).reduce((a, b) => a[1] > b[1] ? a : b);
    schedule.preferredTime = maxSlot[0];
  }

  return schedule;
}

/**
 * Analyzes study streak patterns
 * @param {Object} studyStreakData - Study streak data
 * @param {Object} activityData - General activity data
 * @returns {Object} Streak analysis
 */
export function analyzeStudyStreak(studyStreakData, activityData) {
  const currentStreak = studyStreakData?.currentStreak || 0;
  const longestStreak = studyStreakData?.longestStreak || 0;

  let pattern = 'irregular';
  if (currentStreak >= 14) pattern = 'consistent';
  else if (currentStreak >= 7) pattern = 'building';

  const motivationLevel = Math.min(100, currentStreak * 7);

  return {
    currentStreak,
    longestStreak,
    pattern,
    motivationLevel: Math.round(motivationLevel),
    streakBreaks: [] // Would need historical data
  };
}

/**
 * Analyzes todo task behavior
 * @param {Object} todoTasksData - Todo tasks data
 * @returns {Object} Task behavior analysis
 */
export function analyzeTodoTaskBehavior(todoTasksData) {
  if (!todoTasksData || !todoTasksData.tasks || todoTasksData.tasks.length === 0) {
    return {
      totalTasks: 0,
      completedTasks: 0,
      completionRate: 0,
      averageCompletionTime: 0,
      taskCategories: [],
      procrastinationScore: 0,
      taskConsistency: 'irregular'
    };
  }

  const tasks = todoTasksData.tasks;
  const completedTasks = tasks.filter(t => t.completed).length;
  const completionRate = Math.round((completedTasks / tasks.length) * 100);

  // Calculate average completion time
  let totalCompletionTime = 0;
  let completionCount = 0;

  tasks.forEach(task => {
    if (task.completed && task.completedAt && task.createdAt) {
      const created = new Date(task.createdAt);
      const completed = new Date(task.completedAt);
      const hours = (completed - created) / (1000 * 60 * 60);
      totalCompletionTime += hours;
      completionCount++;
    }
  });

  const averageCompletionTime = completionCount > 0 
    ? Math.round(totalCompletionTime / completionCount) 
    : 0;

  // Categorize tasks
  const categories = {};
  tasks.forEach(task => {
    const category = task.category || 'General';
    if (!categories[category]) {
      categories[category] = { total: 0, completed: 0 };
    }
    categories[category].total++;
    if (task.completed) categories[category].completed++;
  });

  const taskCategories = Object.entries(categories).map(([category, data]) => ({
    category,
    count: data.total,
    completionRate: Math.round((data.completed / data.total) * 100)
  }));

  // Determine consistency
  let taskConsistency = 'irregular';
  if (tasks.length >= 20 && completionRate >= 70) {
    taskConsistency = 'daily';
  } else if (tasks.length >= 10 && completionRate >= 60) {
    taskConsistency = 'weekly';
  }

  return {
    totalTasks: tasks.length,
    completedTasks,
    completionRate,
    averageCompletionTime,
    taskCategories,
    procrastinationScore: 100 - completionRate,
    taskConsistency
  };
}

/**
 * Analyzes course engagement
 * @param {Object} courseData - Course enrollment data
 * @returns {Object} Course engagement analysis
 */
export function analyzeCourseEngagement(courseData) {
  if (!courseData || !courseData.courses || courseData.courses.length === 0) {
    return {
      totalCourses: 0,
      activeCourses: 0,
      completedCourses: 0,
      courseProgress: [],
      averageCourseProgress: 0,
      courseCompletionRate: 0
    };
  }

  const courses = courseData.courses;
  const activeCourses = courses.filter(c => c.status === 'active').length;
  const completedCourses = courses.filter(c => c.status === 'completed').length;

  const courseProgress = courses.map(course => {
    const progress = course.progress || 0;
    const lastAccessed = course.lastAccessed || course.enrolledAt;
    
    // Determine engagement level
    const daysSinceAccess = Math.floor(
      (new Date() - new Date(lastAccessed)) / (1000 * 60 * 60 * 24)
    );
    
    let engagementLevel = 'low';
    if (daysSinceAccess <= 3 && progress > 50) engagementLevel = 'high';
    else if (daysSinceAccess <= 7 && progress > 20) engagementLevel = 'medium';

    return {
      courseId: course.courseId,
      courseName: course.courseName || 'Unknown Course',
      progress,
      lastAccessed,
      engagementLevel
    };
  });

  const averageCourseProgress = courses.length > 0
    ? Math.round(courses.reduce((sum, c) => sum + (c.progress || 0), 0) / courses.length)
    : 0;

  const courseCompletionRate = courses.length > 0
    ? Math.round((completedCourses / courses.length) * 100)
    : 0;

  return {
    totalCourses: courses.length,
    activeCourses,
    completedCourses,
    courseProgress,
    averageCourseProgress,
    courseCompletionRate
  };
}

/**
 * Analyzes counselling impact
 * @param {Object} counsellingData - Counselling session data
 * @param {Object} behavioralData - Behavioral metrics
 * @param {Object} academicData - Academic performance data
 * @returns {Object} Counselling impact analysis
 */
export function analyzeCounsellingImpact(counsellingData, behavioralData, academicData) {
  if (!counsellingData || !counsellingData.sessions || counsellingData.sessions.length === 0) {
    return {
      totalSessions: 0,
      lastSessionDate: null,
      sessionFrequency: 'none',
      topicsDiscussed: [],
      followUpActions: [],
      counsellingImpact: {
        behavioralImprovement: 0,
        academicImprovement: 0
      }
    };
  }

  const sessions = counsellingData.sessions;
  const lastSessionDate = sessions[0]?.sessionDate || null;

  // Determine frequency
  let sessionFrequency = 'rare';
  if (sessions.length >= 8) sessionFrequency = 'regular';
  else if (sessions.length >= 4) sessionFrequency = 'occasional';

  // Extract topics
  const topicsMap = {};
  sessions.forEach(session => {
    const topics = session.topics || [];
    topics.forEach(topic => {
      topicsMap[topic] = (topicsMap[topic] || 0) + 1;
    });
  });

  const topicsDiscussed = Object.entries(topicsMap)
    .map(([topic, frequency]) => ({ topic, frequency }))
    .sort((a, b) => b.frequency - a.frequency)
    .slice(0, 5);

  // Placeholder impact metrics (would need before/after comparison)
  const counsellingImpact = {
    behavioralImprovement: 15,
    academicImprovement: 10
  };

  return {
    totalSessions: sessions.length,
    lastSessionDate,
    sessionFrequency,
    topicsDiscussed,
    followUpActions: [],
    counsellingImpact
  };
}

/**
 * Compares profile settings with actual behavior
 * @param {Object} profileSettings - User's self-reported settings
 * @param {Object} analyzedBehavior - Analyzed behavioral patterns
 * @returns {Object} Comparison and discrepancies
 */
export function compareProfileWithBehavior(profileSettings, analyzedBehavior) {
  if (!profileSettings || !profileSettings.profile) {
    return {
      hasProfileData: false,
      discrepancies: [],
      alignments: [],
      alignmentScore: 0
    };
  }

  const { profile } = profileSettings;
  const discrepancies = [];
  const alignments = [];

  // Compare preferred study time
  if (profile.preferredStudyTime && analyzedBehavior.studySchedule) {
    const stated = profile.preferredStudyTime;
    const actual = analyzedBehavior.studySchedule.preferredTime;
    
    if (stated !== actual) {
      discrepancies.push({
        type: 'study_time',
        aspect: 'Study Time Preference',
        stated,
        actual,
        severity: 'medium',
        message: `You prefer ${stated} study sessions, but you're most active during ${actual}`,
        recommendation: 'Try scheduling more study sessions during your preferred time for better focus'
      });
    } else {
      alignments.push({
        type: 'study_time',
        aspect: 'Study Time Preference',
        message: 'You study during your preferred time slots'
      });
    }
  }

  // Compare study habits with consistency
  if (profile.studyHabits && analyzedBehavior.studyConsistency) {
    const statedHabit = profile.studyHabits.toLowerCase();
    const actualPattern = analyzedBehavior.studyConsistency.pattern;
    
    const habitMapping = {
      'daily': 'consistent',
      'regular': 'consistent',
      'consistent': 'consistent',
      'irregular': 'inconsistent',
      'occasional': 'inconsistent'
    };
    
    const expectedPattern = habitMapping[statedHabit] || statedHabit;
    
    if (expectedPattern !== actualPattern) {
      discrepancies.push({
        type: 'study_consistency',
        aspect: 'Study Consistency',
        stated: statedHabit,
        actual: actualPattern,
        severity: 'high',
        message: `You described your study habits as "${statedHabit}", but your actual pattern is ${actualPattern}`,
        recommendation: 'Build a more consistent study routine to match your intended habits'
      });
    } else {
      alignments.push({
        type: 'study_consistency',
        aspect: 'Study Consistency',
        message: 'Your study habits match your self-assessment'
      });
    }
  }

  // Compare self-reported strengths with actual performance
  if (profile.strengths && profile.strengths.length > 0 && analyzedBehavior.topicPerformance) {
    profile.strengths.forEach(statedStrength => {
      const actualPerformance = analyzedBehavior.topicPerformance?.[statedStrength];
      
      if (actualPerformance && actualPerformance.score < 60) {
        discrepancies.push({
          type: 'strength_mismatch',
          aspect: 'Subject Strength',
          stated: statedStrength,
          actual: `${actualPerformance.score}% accuracy`,
          severity: 'medium',
          message: `You identified ${statedStrength} as a strength, but your performance is ${actualPerformance.score}%`,
          recommendation: `Focus on improving ${statedStrength} or reassess your strengths`
        });
      } else if (actualPerformance && actualPerformance.score >= 70) {
        alignments.push({
          type: 'strength_match',
          aspect: 'Subject Strength',
          message: `Your strength in ${statedStrength} is confirmed by ${actualPerformance.score}% performance`
        });
      }
    });
  }

  // Compare engagement level with self-perception
  if (profile.engagementLevel && analyzedBehavior.engagementLevel) {
    const stated = profile.engagementLevel.toLowerCase();
    const actual = analyzedBehavior.engagementLevel.level;
    
    if (stated !== actual) {
      discrepancies.push({
        type: 'engagement',
        aspect: 'Engagement Level',
        stated,
        actual,
        severity: 'low',
        message: `You rated yourself as "${stated}" engaged, but your activity suggests ${actual} engagement`,
        recommendation: actual === 'low' ? 'Increase participation in doubts, assignments, and classes' : 'Your engagement is higher than you think!'
      });
    } else {
      alignments.push({
        type: 'engagement',
        aspect: 'Engagement Level',
        message: 'Your self-assessed engagement matches your actual activity'
      });
    }
  }

  // Procrastination tendency
  if (profile.procrastinationTendency && analyzedBehavior.procrastinationIndex) {
    const stated = profile.procrastinationTendency; // 'low', 'medium', 'high'
    const actualIndex = analyzedBehavior.procrastinationIndex.index;
    
    let actualLevel = 'low';
    if (actualIndex > 60) actualLevel = 'high';
    else if (actualIndex > 30) actualLevel = 'medium';
    
    if (stated !== actualLevel) {
      discrepancies.push({
        type: 'procrastination',
        aspect: 'Procrastination',
        stated,
        actual: actualLevel,
        severity: actualLevel === 'high' ? 'high' : 'medium',
        message: `You rated your procrastination as "${stated}", but data shows ${actualLevel} procrastination (${actualIndex}%)`,
        recommendation: 'Set earlier deadlines and break tasks into smaller chunks'
      });
    } else {
      alignments.push({
        type: 'procrastination',
        aspect: 'Procrastination',
        message: 'Your self-awareness about procrastination is accurate'
      });
    }
  }

  const alignmentScore = alignments.length > 0 
    ? Math.round((alignments.length / (alignments.length + discrepancies.length)) * 100)
    : Math.max(0, 100 - (discrepancies.length * 15));

  return {
    hasProfileData: true,
    discrepancies,
    alignments,
    alignmentScore,
    totalComparisons: alignments.length + discrepancies.length,
    profileUtilization: calculateProfileUtilization(profile)
  };
}

/**
 * Calculates how much of the profile data is being utilized
 */
function calculateProfileUtilization(profile) {
  if (!profile) {
    return {
      percentage: 0,
      filledFields: 0,
      totalFields: 0,
      missingFields: []
    };
  }

  const utilizableFields = [
    'preferredStudyTime',
    'studyHabits',
    'learningPreferences',
    'strengths',
    'weaknesses',
    'goals',
    'engagementLevel',
    'procrastinationTendency'
  ];

  // Also check for basic profile data
  const hasBasicProfile = !!(
    profile.personalInfo?.firstName ||
    profile.contactInfo?.email ||
    profile.results?.class10 ||
    profile.results?.class12
  );
  
  let filled = 0;
  utilizableFields.forEach(field => {
    if (profile[field] && (Array.isArray(profile[field]) ? profile[field].length > 0 : profile[field])) {
      filled++;
    }
  });

  // Add bonus for having basic profile filled
  if (hasBasicProfile) {
    filled += 1;
  }
  
  const totalFields = utilizableFields.length + 1; // +1 for basic profile
  
  return {
    percentage: Math.round((filled / totalFields) * 100),
    filledFields: filled,
    totalFields: totalFields,
    missingFields: utilizableFields.filter(field => 
      !profile[field] || (Array.isArray(profile[field]) && profile[field].length === 0)
    ),
    hasBasicProfile
  };
}

/**
 * Analyzes detailed test behavior including time management and accuracy patterns
 * @param {Array} testResults - Test results data
 * @returns {Object} Detailed test behavior analysis
 */
export function analyzeTestBehavior(testResults) {
  if (!testResults || testResults.length === 0) {
    return {
      timeManagement: {
        averageTimePerQuestion: 0,
        fastQuestions: [],
        slowQuestions: [],
        timeDistribution: {},
        rushingIndicator: 0
      },
      accuracyPatterns: {
        accuracyByTimeSpent: [],
        accuracyDropoffPoint: null,
        optimalTimeRange: null,
        accuracyByAttemptOrder: []
      },
      safeAttemptAnalysis: {
        recommendedAttemptPercentage: 0,
        currentAttemptRate: 0,
        accuracyByAttemptRate: [],
        optimalAttemptStrategy: null
      },
      questionLevelInsights: {
        totalQuestions: 0,
        averageAccuracy: 0,
        topicWiseTimeSpending: [],
        difficultyWisePerformance: []
      }
    };
  }

  // Aggregate all question-level data from tests
  const allQuestions = [];
  testResults.forEach(test => {
    if (test.detailedResults && Array.isArray(test.detailedResults)) {
      test.detailedResults.forEach(q => {
        allQuestions.push({
          ...q,
          testId: test.id,
          testName: test.testName || test.name,
          testDate: test.completedAt || test.createdAt
        });
      });
    }
  });

  // Time Management Analysis
  const timeManagement = analyzeTimeManagement(allQuestions);
  
  // Accuracy Patterns Analysis
  const accuracyPatterns = analyzeAccuracyPatterns(allQuestions);
  
  // Safe Attempt Analysis
  const safeAttemptAnalysis = analyzeSafeAttempts(testResults, allQuestions);
  
  // Question Level Insights
  const questionLevelInsights = analyzeQuestionLevelInsights(allQuestions);

  return {
    timeManagement,
    accuracyPatterns,
    safeAttemptAnalysis,
    questionLevelInsights
  };
}

/**
 * Analyzes time management in tests
 */
function analyzeTimeManagement(questions) {
  if (questions.length === 0) {
    return {
      averageTimePerQuestion: 0,
      fastQuestions: [],
      slowQuestions: [],
      timeDistribution: {},
      rushingIndicator: 0
    };
  }

  // Calculate average time per question
  const questionsWithTime = questions.filter(q => q.timeSpent && q.timeSpent > 0);
  const totalTime = questionsWithTime.reduce((sum, q) => sum + q.timeSpent, 0);
  const averageTimePerQuestion = questionsWithTime.length > 0 
    ? Math.round(totalTime / questionsWithTime.length) 
    : 0;

  // Identify fast and slow questions (by topic)
  const topicTimeMap = {};
  questionsWithTime.forEach(q => {
    const topic = q.topic || q.subject || 'General';
    if (!topicTimeMap[topic]) {
      topicTimeMap[topic] = { times: [], questions: [] };
    }
    topicTimeMap[topic].times.push(q.timeSpent);
    topicTimeMap[topic].questions.push(q);
  });

  const fastQuestions = [];
  const slowQuestions = [];

  Object.entries(topicTimeMap).forEach(([topic, data]) => {
    const avgTime = data.times.reduce((a, b) => a + b, 0) / data.times.length;
    
    // Questions taking < 50% of average time
    const fast = data.questions.filter(q => q.timeSpent < avgTime * 0.5);
    if (fast.length > 0) {
      fastQuestions.push({
        topic,
        count: fast.length,
        averageTime: Math.round(fast.reduce((sum, q) => sum + q.timeSpent, 0) / fast.length),
        accuracy: Math.round((fast.filter(q => q.isCorrect).length / fast.length) * 100)
      });
    }

    // Questions taking > 150% of average time
    const slow = data.questions.filter(q => q.timeSpent > avgTime * 1.5);
    if (slow.length > 0) {
      slowQuestions.push({
        topic,
        count: slow.length,
        averageTime: Math.round(slow.reduce((sum, q) => sum + q.timeSpent, 0) / slow.length),
        accuracy: Math.round((slow.filter(q => q.isCorrect).length / slow.length) * 100)
      });
    }
  });

  // Time distribution by ranges
  const timeDistribution = {
    'under30s': 0,
    '30s-1min': 0,
    '1-2min': 0,
    '2-3min': 0,
    'over3min': 0
  };

  questionsWithTime.forEach(q => {
    const seconds = q.timeSpent;
    if (seconds < 30) timeDistribution['under30s']++;
    else if (seconds < 60) timeDistribution['30s-1min']++;
    else if (seconds < 120) timeDistribution['1-2min']++;
    else if (seconds < 180) timeDistribution['2-3min']++;
    else timeDistribution['over3min']++;
  });

  // Rushing indicator (percentage of questions answered too quickly with low accuracy)
  const rushedQuestions = questionsWithTime.filter(q => 
    q.timeSpent < 30 && !q.isCorrect
  );
  const rushingIndicator = Math.round((rushedQuestions.length / questionsWithTime.length) * 100);

  return {
    averageTimePerQuestion,
    fastQuestions: fastQuestions.sort((a, b) => b.count - a.count).slice(0, 5),
    slowQuestions: slowQuestions.sort((a, b) => b.count - a.count).slice(0, 5),
    timeDistribution,
    rushingIndicator
  };
}

/**
 * Analyzes accuracy patterns based on time spent
 */
function analyzeAccuracyPatterns(questions) {
  if (questions.length === 0) {
    return {
      accuracyByTimeSpent: [],
      accuracyDropoffPoint: null,
      optimalTimeRange: null,
      accuracyByAttemptOrder: []
    };
  }

  const questionsWithTime = questions.filter(q => q.timeSpent && q.timeSpent > 0);

  // Accuracy by time spent (in 30-second buckets)
  const timeBuckets = {};
  questionsWithTime.forEach(q => {
    const bucket = Math.floor(q.timeSpent / 30) * 30; // Round to nearest 30s
    if (!timeBuckets[bucket]) {
      timeBuckets[bucket] = { correct: 0, total: 0 };
    }
    timeBuckets[bucket].total++;
    if (q.isCorrect) timeBuckets[bucket].correct++;
  });

  const accuracyByTimeSpent = Object.entries(timeBuckets)
    .map(([time, data]) => ({
      timeRange: `${time}-${parseInt(time) + 30}s`,
      timeSeconds: parseInt(time),
      accuracy: Math.round((data.correct / data.total) * 100),
      questionCount: data.total
    }))
    .sort((a, b) => a.timeSeconds - b.timeSeconds);

  // Find accuracy dropoff point (where accuracy starts declining significantly)
  let accuracyDropoffPoint = null;
  let optimalTimeRange = null;
  let maxAccuracy = 0;

  for (let i = 0; i < accuracyByTimeSpent.length; i++) {
    const current = accuracyByTimeSpent[i];
    
    // Track optimal time range (highest accuracy with reasonable sample size)
    if (current.accuracy > maxAccuracy && current.questionCount >= 5) {
      maxAccuracy = current.accuracy;
      optimalTimeRange = current.timeRange;
    }

    // Find dropoff point (accuracy drops by 15% or more)
    if (i > 0) {
      const previous = accuracyByTimeSpent[i - 1];
      if (previous.accuracy - current.accuracy >= 15 && current.questionCount >= 3) {
        accuracyDropoffPoint = {
          timeThreshold: current.timeRange,
          accuracyDrop: previous.accuracy - current.accuracy,
          message: `Accuracy drops significantly after ${previous.timeRange}`
        };
        break;
      }
    }
  }

  // Accuracy by attempt order (first 10, second 10, etc.)
  const orderBuckets = {};
  questions.forEach((q, index) => {
    const bucket = Math.floor(index / 10) * 10;
    if (!orderBuckets[bucket]) {
      orderBuckets[bucket] = { correct: 0, total: 0 };
    }
    orderBuckets[bucket].total++;
    if (q.isCorrect) orderBuckets[bucket].correct++;
  });

  const accuracyByAttemptOrder = Object.entries(orderBuckets)
    .map(([order, data]) => ({
      questionRange: `Q${parseInt(order) + 1}-${parseInt(order) + 10}`,
      accuracy: Math.round((data.correct / data.total) * 100),
      questionCount: data.total
    }))
    .sort((a, b) => parseInt(a.questionRange.split('-')[0].substring(1)) - parseInt(b.questionRange.split('-')[0].substring(1)));

  return {
    accuracyByTimeSpent,
    accuracyDropoffPoint,
    optimalTimeRange,
    accuracyByAttemptOrder
  };
}

/**
 * Analyzes safe attempt strategy
 */
function analyzeSafeAttempts(testResults, questions) {
  if (testResults.length === 0) {
    return {
      recommendedAttemptPercentage: 0,
      currentAttemptRate: 0,
      accuracyByAttemptRate: [],
      optimalAttemptStrategy: null
    };
  }

  // Calculate current attempt rate - support both direct fields and detailedResults
  const testsWithAttemptData = testResults.filter(t => {
    // Check if test has direct fields
    if (t.totalQuestions && t.attemptedQuestions !== undefined) {
      return true;
    }
    // Or check if test has detailedResults
    if (t.detailedResults && Array.isArray(t.detailedResults) && t.detailedResults.length > 0) {
      return true;
    }
    return false;
  }).map(t => {
    // Normalize test data to have totalQuestions and attemptedQuestions
    if (t.totalQuestions && t.attemptedQuestions !== undefined) {
      return t;
    }
    // Calculate from detailedResults
    const totalQuestions = t.detailedResults.length;
    const attemptedQuestions = t.detailedResults.filter(q => 
      q.selectedAnswer !== undefined && q.selectedAnswer !== null && q.selectedAnswer !== ''
    ).length;
    
    return {
      ...t,
      totalQuestions,
      attemptedQuestions
    };
  });

  let currentAttemptRate = 0;
  if (testsWithAttemptData.length > 0) {
    const totalAttemptRate = testsWithAttemptData.reduce((sum, t) => {
      return sum + ((t.attemptedQuestions / t.totalQuestions) * 100);
    }, 0);
    currentAttemptRate = Math.round(totalAttemptRate / testsWithAttemptData.length);
  }

  // Analyze accuracy by attempt rate (group tests by attempt percentage)
  const attemptRateBuckets = {};
  testsWithAttemptData.forEach(test => {
    const attemptRate = Math.round((test.attemptedQuestions / test.totalQuestions) * 100);
    const bucket = Math.floor(attemptRate / 10) * 10; // 0-10%, 10-20%, etc.
    
    if (!attemptRateBuckets[bucket]) {
      attemptRateBuckets[bucket] = { 
        scores: [], 
        accuracies: [],
        testCount: 0 
      };
    }
    
    attemptRateBuckets[bucket].scores.push(test.score || test.percentageScore || 0);
    attemptRateBuckets[bucket].accuracies.push(test.accuracy || 0);
    attemptRateBuckets[bucket].testCount++;
  });

  const accuracyByAttemptRate = Object.entries(attemptRateBuckets)
    .map(([rate, data]) => ({
      attemptRange: `${rate}-${parseInt(rate) + 10}%`,
      attemptPercentage: parseInt(rate),
      averageScore: Math.round(data.scores.reduce((a, b) => a + b, 0) / data.scores.length),
      averageAccuracy: Math.round(data.accuracies.reduce((a, b) => a + b, 0) / data.accuracies.length),
      testCount: data.testCount
    }))
    .sort((a, b) => a.attemptPercentage - b.attemptPercentage);

  // Find optimal attempt strategy (highest score with good sample size)
  let optimalAttemptStrategy = null;
  let maxScore = 0;

  accuracyByAttemptRate.forEach(bucket => {
    if (bucket.testCount >= 2 && bucket.averageScore > maxScore) {
      maxScore = bucket.averageScore;
      optimalAttemptStrategy = {
        attemptRange: bucket.attemptRange,
        expectedScore: bucket.averageScore,
        expectedAccuracy: bucket.averageAccuracy,
        recommendation: generateAttemptRecommendation(bucket.attemptPercentage, bucket.averageAccuracy)
      };
    }
  });

  // Calculate recommended attempt percentage based on accuracy patterns
  let recommendedAttemptPercentage = 0;
  if (accuracyByAttemptRate.length > 0) {
    // Find the sweet spot: highest accuracy with reasonable attempt rate
    const goodBuckets = accuracyByAttemptRate.filter(b => 
      b.averageAccuracy >= 70 && b.testCount >= 2
    );
    
    if (goodBuckets.length > 0) {
      // Recommend the highest attempt rate that maintains good accuracy
      const best = goodBuckets[goodBuckets.length - 1];
      recommendedAttemptPercentage = best.attemptPercentage + 5; // Add 5% buffer
    } else {
      // If no good buckets, recommend conservative approach
      recommendedAttemptPercentage = 70;
    }
  }

  return {
    recommendedAttemptPercentage: Math.min(100, recommendedAttemptPercentage),
    currentAttemptRate,
    accuracyByAttemptRate,
    optimalAttemptStrategy
  };
}

/**
 * Generates attempt recommendation message
 */
function generateAttemptRecommendation(attemptPercentage, accuracy) {
  if (accuracy >= 80) {
    return `You maintain ${accuracy}% accuracy at ${attemptPercentage}% attempts. Consider attempting more questions to maximize score.`;
  } else if (accuracy >= 70) {
    return `Your accuracy is ${accuracy}% at ${attemptPercentage}% attempts. This is a good balance - maintain this strategy.`;
  } else {
    return `Your accuracy drops to ${accuracy}% at ${attemptPercentage}% attempts. Focus on quality over quantity - attempt fewer questions with higher confidence.`;
  }
}

/**
 * Analyzes question-level insights
 */
function analyzeQuestionLevelInsights(questions) {
  if (questions.length === 0) {
    return {
      totalQuestions: 0,
      averageAccuracy: 0,
      topicWiseTimeSpending: [],
      difficultyWisePerformance: []
    };
  }

  const totalQuestions = questions.length;
  const correctQuestions = questions.filter(q => q.isCorrect).length;
  const averageAccuracy = Math.round((correctQuestions / totalQuestions) * 100);

  // Topic-wise time spending
  const topicMap = {};
  questions.forEach(q => {
    const topic = q.topic || q.subject || 'General';
    if (!topicMap[topic]) {
      topicMap[topic] = {
        totalTime: 0,
        questionCount: 0,
        correct: 0,
        timeSpentQuestions: 0
      };
    }
    topicMap[topic].questionCount++;
    if (q.isCorrect) topicMap[topic].correct++;
    if (q.timeSpent) {
      topicMap[topic].totalTime += q.timeSpent;
      topicMap[topic].timeSpentQuestions++;
    }
  });

  const topicWiseTimeSpending = Object.entries(topicMap)
    .map(([topic, data]) => ({
      topic,
      averageTime: data.timeSpentQuestions > 0 
        ? Math.round(data.totalTime / data.timeSpentQuestions) 
        : 0,
      accuracy: Math.round((data.correct / data.questionCount) * 100),
      questionCount: data.questionCount,
      totalTimeSpent: Math.round(data.totalTime / 60), // Convert to minutes
      recommendation: generateTopicRecommendation(
        data.timeSpentQuestions > 0 ? data.totalTime / data.timeSpentQuestions : 0,
        (data.correct / data.questionCount) * 100
      )
    }))
    .sort((a, b) => b.totalTimeSpent - a.totalTimeSpent);

  // Difficulty-wise performance
  const difficultyMap = {};
  questions.forEach(q => {
    const difficulty = q.difficulty || 'medium';
    if (!difficultyMap[difficulty]) {
      difficultyMap[difficulty] = {
        totalTime: 0,
        questionCount: 0,
        correct: 0,
        timeSpentQuestions: 0
      };
    }
    difficultyMap[difficulty].questionCount++;
    if (q.isCorrect) difficultyMap[difficulty].correct++;
    if (q.timeSpent) {
      difficultyMap[difficulty].totalTime += q.timeSpent;
      difficultyMap[difficulty].timeSpentQuestions++;
    }
  });

  const difficultyWisePerformance = Object.entries(difficultyMap)
    .map(([difficulty, data]) => ({
      difficulty,
      averageTime: data.timeSpentQuestions > 0 
        ? Math.round(data.totalTime / data.timeSpentQuestions) 
        : 0,
      accuracy: Math.round((data.correct / data.questionCount) * 100),
      questionCount: data.questionCount
    }))
    .sort((a, b) => {
      const order = { 'easy': 1, 'medium': 2, 'hard': 3 };
      return (order[a.difficulty] || 2) - (order[b.difficulty] || 2);
    });

  return {
    totalQuestions,
    averageAccuracy,
    topicWiseTimeSpending: topicWiseTimeSpending.slice(0, 10),
    difficultyWisePerformance
  };
}

/**
 * Generates topic-specific recommendation
 */
function generateTopicRecommendation(avgTime, accuracy) {
  if (avgTime > 180 && accuracy < 60) {
    return 'High time, low accuracy - needs focused practice and concept clarity';
  } else if (avgTime > 180 && accuracy >= 60) {
    return 'Taking too much time - practice speed with timed drills';
  } else if (avgTime < 60 && accuracy < 60) {
    return 'Rushing through - slow down and read questions carefully';
  } else if (avgTime < 60 && accuracy >= 80) {
    return 'Strong area - maintain speed and accuracy';
  } else if (accuracy >= 80) {
    return 'Good performance - keep practicing regularly';
  } else {
    return 'Needs improvement - focus on concept building';
  }
}

/**
 * Generates comprehensive behavioral profile
 * @param {Object} allData - All aggregated student data
 * @returns {Object} Complete behavioral profile
 */
export function generateBehavioralProfile(allData) {
  const studyConsistency = analyzeStudyConsistency(
    allData.attendanceData,
    allData.revisionData,
    allData.studyStreakData
  );

  const engagementLevel = analyzeEngagementLevel(
    allData.doubtData,
    allData.attendanceData,
    allData.assignmentData
  );

  const procrastinationIndex = detectProcrastination(
    allData.assignmentData,
    allData.todoTasksData
  );

  const studySchedule = analyzeStudySchedule(
    allData.revisionData,
    allData.testData?.testResults
  );

  const studyStreakAnalysis = analyzeStudyStreak(
    allData.studyStreakData,
    null
  );

  const todoTaskAnalysis = analyzeTodoTaskBehavior(allData.todoTasksData);
  const courseEngagement = analyzeCourseEngagement(allData.courseData);
  const counsellingImpact = analyzeCounsellingImpact(
    allData.counsellingData,
    null,
    null
  );

  const profileComparison = compareProfileWithBehavior(
    allData.profileSettings,
    { 
      studySchedule,
      studyConsistency,
      engagementLevel,
      procrastinationIndex
    }
  );

  // NEW: Detailed test behavior analysis
  const testBehaviorAnalysis = analyzeTestBehavior(
    allData.testData?.testResults || []
  );

  // Calculate attendance rate
  let attendanceRate = 0;
  if (allData.attendanceData && allData.attendanceData.records && allData.attendanceData.records.length > 0) {
    const totalAttendance = allData.attendanceData.records.reduce((sum, record) => {
      return sum + (record.attendance || 0);
    }, 0);
    attendanceRate = Math.round(totalAttendance / allData.attendanceData.records.length);
  }

  // Calculate assignment completion rate
  let assignmentCompletionRate = 0;
  if (allData.assignmentData && allData.assignmentData.submissions) {
    assignmentCompletionRate = allData.assignmentData.submissions.length > 0 ? 100 : 0;
  }

  return {
    studyConsistency,
    engagementLevel,
    procrastinationIndex,
    attendanceRate,
    assignmentCompletionRate,
    studySchedule,
    studyStreakAnalysis,
    todoTaskAnalysis,
    courseEngagement,
    counsellingImpact,
    profileComparison,
    testBehaviorAnalysis // NEW: Detailed test behavior insights
  };
}
